import CircleBg from "@assets/icons/circles-bg-register.svg";
import styled from "@emotion/styled";
import { ContactSupport } from "@ui/components/buttons/contact-support";
import { ResponsiveDeviceType, useDimensions } from "@ui/hooks/use-dimensions";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import { svgComponentToCSS } from "@utils/svg-utils";
import React, { useMemo } from "react";
import { useResizeDetector } from "react-resize-detector";

interface RegisterContainerProps {
	form: JSX.Element;
	children: JSX.Element;
}

const RESPONSIVE_PADDING: Record<ResponsiveDeviceType, number> = {
	[ResponsiveDeviceType.Desktop]: 24,
	[ResponsiveDeviceType.Tablet]: 32,
	[ResponsiveDeviceType.Mobile]: 16,
};

export const RegisterContainer: React.FC<RegisterContainerProps> = ({ form, children }) => {
	const { height, ref } = useResizeDetector();
	const { responsiveDeviceType, isMobile } = useDimensions();

	const backgroundOffset = useMemo(() => {
		if (isNil(height)) {
			return 0;
		}
		return height + RESPONSIVE_PADDING[responsiveDeviceType] * 2;
	}, [height, responsiveDeviceType]);

	return (
		<Container>
			<FormContainer ref={ref}>
				{isMobile && (
					<RowContactSupport>
						<SubnavigationContainer>
							<ContactSupport uiStyle="secondary" />
						</SubnavigationContainer>
					</RowContactSupport>
				)}
				<FormCard>{form}</FormCard>
			</FormContainer>
			{children}
			<BackgroundCircles insetTop={backgroundOffset} />
		</Container>
	);
};

const Container = styled.div`
	background-color: ${colors.primary10};
	min-height: 100vh;
	width: 100%;
	max-width: 100vw;
	flex: 1;
	display: flex;
	flex-direction: row;
	padding: ${RESPONSIVE_PADDING[ResponsiveDeviceType.Desktop]}px;
	gap: 56px;
	position: relative;
	z-index: 1;
	align-items: flex-start;

	@media ${physioMeTabletBreakpoint} {
		flex-direction: column;
		padding: 0 0 100px 0;
		gap: 0;
		overflow: hidden;
		align-items: initial;
	}
`;

const RowContactSupport = styled.span`
	${stack(8, undefined, "stretch")}
	margin-bottom: 50px;
`;

const SubnavigationContainer = styled.span`
	align-self: start;
	position: absolute;
	top: 0px;
	right: 0px;

	@media ${physioMeMobileBreakpoint} {
		margin-top: 24px;
		margin-right: 16px;
	}
`;

const FormContainer = styled.div`
	width: 50%;
	@media ${physioMeTabletBreakpoint} {
		padding: ${RESPONSIVE_PADDING[ResponsiveDeviceType.Tablet]}px 16px;
		background-color: ${colors.white};
		width: 100%;
	}
	@media ${physioMeMobileBreakpoint} {
		padding: ${RESPONSIVE_PADDING[ResponsiveDeviceType.Mobile]}px;
	}
`;

const FormCard = styled.div`
	flex: 1;
	display: flex;
	border-radius: 40px;
	background-color: ${colors.white};
	padding: 80px 40px;
	justify-content: center;
	@media ${physioMeTabletBreakpoint} {
		padding: 0 0;
		min-width: 500px;
	}
	@media ${physioMeMobileBreakpoint} {
		padding: 0 16px;
		min-width: unset;
	}
`;

const BackgroundCircles = styled.div<{ insetTop: number }>`
	background-image: ${svgComponentToCSS(<CircleBg width={1459} height="100%" />)};
	height: 100%;
	width: 50%;
	position: fixed;
	background-repeat: no-repeat;
	right: 0px;
	bottom: 0px;
	-webkit-background-position: right bottom;
	background-position: initial;
	z-index: -2;
	@media ${physioMeTabletBreakpoint} {
		top: ${({ insetTop }) => insetTop}px;
		background-position-x: 50%;
		width: 100%;
		position: absolute;
	}

	@media ${physioMeMobileBreakpoint} {
		background-image: ${svgComponentToCSS(<CircleBg width={1459} height="100%" />)};
		left: 0px;
		top: 890px;
	}
`;
